import { useRef, useState, useEffect } from "react";
import io from "socket.io-client";

const PCM_WORKLET_URL = "pcm-processor.js"; // Adjust path if needed

export function useSpeechToText(
  socketUrl = "https://api.storytent.in",
  languageLabel = "English"
) {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState(""); // Final transcript
  const [interimTranscript, setInterimTranscript] = useState(""); // Interim transcript
  const [error, setError] = useState("");
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRef = useRef(null);
  const socketRef = useRef(null);
  const pcmBufferRef = useRef([]); // For batching PCM samples
  const PCM_CHUNK_SIZE = 4096; // samples (256ms at 16kHz)

  const getLanguageCode = (label) => {
    if (!label) return "en-US";
    if (label === "Eng" || label === "English") return "en-US";
    if (label === "हिन्दी") return "hi-IN";
    return "en-US";
  };

  useEffect(() => {
    // console.log("[SpeechToText] Connecting to socket at", socketUrl);
    socketRef.current = io(socketUrl, {
      transports: ["websocket"],
      withCredentials: true,
    });

    socketRef.current.on("connect", () => {
      // console.log("[SpeechToText] Socket connected, id:", socketRef.current.id);
    });

    socketRef.current.on("disconnect", () => {
      // console.log("[SpeechToText] Socket disconnected");
    });

    socketRef.current.on("speechResult", (data) => {
      // data: { transcript, isFinal }
      if (data.isFinal) {
        setTranscript((prev) => prev + (data.transcript || ""));
        setInterimTranscript("");
      } else {
        setInterimTranscript(data.transcript || "");
      }
    });

    socketRef.current.on("speechError", (err) => {
      console.error("[SpeechToText] Received speechError:", err);
      setError("Speech error: " + err);
    });

    return () => {
      // console.log("[SpeechToText] Disconnecting socket");
      socketRef.current.disconnect();
    };
  }, [socketUrl]);

  const startRecording = async () => {
    setTranscript("");
    setIsRecording(true);
    setError("");
    // console.log("[SpeechToText] Starting recording (AudioWorklet)");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioContext = new window.AudioContext({ sampleRate: 16000 });
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;

      // Load the worklet if not already loaded
      if (!audioContext.audioWorklet.workletLoaded) {
        await audioContext.audioWorklet.addModule(PCM_WORKLET_URL);
        audioContext.audioWorklet.workletLoaded = true;
      }
      const pcmNode = new window.AudioWorkletNode(
        audioContext,
        "pcm-processor"
      );
      processorRef.current = pcmNode;

      pcmBufferRef.current = [];

      pcmNode.port.onmessage = (event) => {
        if (event.data && event.data.type === "pcm") {
          // Accumulate Int16 samples
          const newSamples = new Int16Array(event.data.buffer);
          pcmBufferRef.current.push(...newSamples);
          // If enough samples, send chunk
          while (pcmBufferRef.current.length >= PCM_CHUNK_SIZE) {
            const chunk = pcmBufferRef.current.slice(0, PCM_CHUNK_SIZE);
            socketRef.current.emit("audioData", new Int16Array(chunk).buffer);
            pcmBufferRef.current = pcmBufferRef.current.slice(PCM_CHUNK_SIZE);
          }
        }
      };

      socketRef.current.emit("startSpeechRecognition", {
        encoding: "LINEAR16",
        sampleRateHertz: 16000,
        languageCode: getLanguageCode(languageLabel),
      });
      // console.log("[SpeechToText] Emitted startSpeechRecognition");

      source.connect(pcmNode);
      pcmNode.connect(audioContext.destination);
    } catch (err) {
      console.error("[SpeechToText] Error starting recording:", err);
      setError("Microphone access denied or not available.");
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    setIsRecording(false);
    // Cleanup audio nodes and context
    if (processorRef.current) {
      processorRef.current.disconnect();
      if (processorRef.current.port) {
        processorRef.current.port.onmessage = null;
      }
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    pcmBufferRef.current = [];
    socketRef.current.emit("endSpeechRecognition");
    // console.log(
    //   "[SpeechToText] Emitted endSpeechRecognition and cleaned up audio context"
    // );
  };

  return {
    isRecording,
    transcript,
    interimTranscript,
    startRecording,
    stopRecording,
    error,
  };
}
